import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import supportTicketService from '../../services/supportTicketService';
import ConfirmDeleteModal from '../Common/ConfirmDeleteModal/ConfirmDeleteModal';
import TicketEvidenceViewer from './TicketEvidenceViewer';
import { TICKET_STATUSES, formatTicketDate, dispatchTicketNotificationsRefresh } from './supportTicketUtils';
import './SupportTicketPanel.css';

const CLOSE_CONFIRM_MESSAGE =
  'Are you sure you want to close this ticket? Closing will permanently delete this ticket record and all uploaded images.';

const SupportTicketsScreen = ({ user, onOpenSubmitPanel }) => {
  const isSuperAdmin = Number(user?.role_id) === 1;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [rejectionDrafts, setRejectionDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [closeConfirm, setCloseConfirm] = useState(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const result = await supportTicketService.getMyTickets();
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to load tickets.');
      }
      const rows = result.data || [];
      setTickets(rows);
      const nextStatusDrafts = {};
      const nextRejectionDrafts = {};
      rows.forEach((ticket) => {
        nextStatusDrafts[ticket.id] = ticket.status;
        nextRejectionDrafts[ticket.id] = ticket.rejection_reason || '';
      });
      setStatusDrafts(nextStatusDrafts);
      setRejectionDrafts(nextRejectionDrafts);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  const patchTicketInState = useCallback((updatedTicket) => {
    if (!updatedTicket?.id) return;
    setTickets((prev) =>
      prev.map((t) =>
        Number(t.id) === Number(updatedTicket.id) ? { ...t, ...updatedTicket } : t
      )
    );
    setStatusDrafts((prev) => ({ ...prev, [updatedTicket.id]: updatedTicket.status }));
    setRejectionDrafts((prev) => ({
      ...prev,
      [updatedTicket.id]: updatedTicket.rejection_reason || '',
    }));
  }, []);

  const removeTicketFromState = useCallback((ticketId) => {
    setTickets((prev) => prev.filter((t) => Number(t.id) !== Number(ticketId)));
    setStatusDrafts((prev) => {
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
    setRejectionDrafts((prev) => {
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
    setExpandedId((prev) => (Number(prev) === Number(ticketId) ? null : prev));
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const applyStatusUpdate = async (ticketId, statusOverride) => {
    const ticket = tickets.find((t) => Number(t.id) === Number(ticketId));
    const status = statusOverride ?? statusDrafts[ticketId] ?? ticket?.status;
    const rejectionReason = rejectionDrafts[ticketId];

    if (!status || status === ticket?.status) {
      return false;
    }
    if (status === 'Rejected' && !String(rejectionReason || '').trim()) {
      toast.error('Rejection reason is required.');
      setExpandedId(ticketId);
      return false;
    }

    setSavingId(ticketId);
    try {
      const result = await supportTicketService.updateTicketStatus(ticketId, {
        status,
        rejection_reason: rejectionReason,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to update ticket.');
      }

      if (result.data?.deleted || status === 'Closed') {
        removeTicketFromState(ticketId);
        dispatchTicketNotificationsRefresh();
        toast.success(
          result.message ||
            'Ticket closed. The record and uploaded images have been permanently deleted.'
        );
        return true;
      }

      patchTicketInState(result.data);
      dispatchTicketNotificationsRefresh();
      if (status === 'Rejected') {
        toast.success('Rejection submitted. The submitter has been notified by email.');
      } else {
        toast.success('Status updated. The submitter has been notified by email.');
      }
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to update ticket.');
      if (ticket) {
        setStatusDrafts((prev) => ({ ...prev, [ticketId]: ticket.status }));
      }
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleStatusSelectChange = (ticket, newStatus) => {
    if (newStatus === ticket.status) {
      setStatusDrafts((prev) => ({ ...prev, [ticket.id]: newStatus }));
      return;
    }

    if (newStatus === 'Closed') {
      setStatusDrafts((prev) => ({ ...prev, [ticket.id]: ticket.status }));
      setCloseConfirm({ ticketId: ticket.id, mode: 'status' });
      return;
    }

    setStatusDrafts((prev) => ({ ...prev, [ticket.id]: newStatus }));

    if (newStatus === 'Rejected') {
      setExpandedId(ticket.id);
      return;
    }

    applyStatusUpdate(ticket.id, newStatus);
  };

  const handleSubmitRejection = (ticket) => {
    const reason = String(rejectionDrafts[ticket.id] || '').trim();
    if (!reason) {
      toast.error('Rejection reason is required.');
      return;
    }
    applyStatusUpdate(ticket.id, 'Rejected');
  };

  const handleVerifyClose = (ticketId) => {
    setCloseConfirm({ ticketId, mode: 'verify' });
  };

  const handleCancelCloseConfirm = () => {
    if (savingId || closingId) return;
    setCloseConfirm(null);
  };

  const runVerifyClose = async (ticketId) => {
    setClosingId(ticketId);
    try {
      const result = await supportTicketService.verifyCloseTicket(ticketId);
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to close ticket.');
      }
      removeTicketFromState(ticketId);
      dispatchTicketNotificationsRefresh();
      toast.success(
        result.message ||
          'Ticket closed. The record and uploaded images have been permanently deleted.'
      );
      setCloseConfirm(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to close ticket.');
    } finally {
      setClosingId(null);
    }
  };

  const handleConfirmClose = async () => {
    if (!closeConfirm?.ticketId) return;
    const { ticketId, mode } = closeConfirm;
    if (mode === 'verify') {
      await runVerifyClose(ticketId);
      return;
    }
    const ok = await applyStatusUpdate(ticketId, 'Closed');
    if (ok) {
      setCloseConfirm(null);
    }
  };

  const closeConfirmTicket = closeConfirm
    ? tickets.find((t) => Number(t.id) === Number(closeConfirm.ticketId))
    : null;
  const isCloseBusy = Boolean(
    closeConfirm &&
      (Number(savingId) === Number(closeConfirm.ticketId) ||
        Number(closingId) === Number(closeConfirm.ticketId))
  );

  return (
    <div className="support-tickets-page">
      <header className="support-tickets-page-top">
        <h1 className="support-tickets-page-title">
          {isSuperAdmin ? 'Support Ticket Admin' : 'My Support Tickets'}
        </h1>
        <p className="support-tickets-page-subtitle">
          {isSuperAdmin
            ? 'Review, triage, and update support tickets from all users.'
            : 'Track your submitted tickets and verify fixes when deployed.'}
        </p>
      </header>

      <div className="support-tickets-view-tabs" role="tablist" aria-label="Support tickets">
        <button type="button" role="tab" aria-selected className="support-tickets-view-tab active">
          {isSuperAdmin ? 'All Tickets' : 'My Tickets'}
        </button>
        {!isSuperAdmin && (
          <button
            type="button"
            role="tab"
            className="support-tickets-view-tab"
            onClick={() => onOpenSubmitPanel?.()}
          >
            Submit Support Ticket
          </button>
        )}
      </div>

      <div className="support-tickets-page-body">
        {loading ? (
          <div className="support-tickets-empty">Loading tickets…</div>
        ) : tickets.length === 0 ? (
          <div className="support-tickets-empty">
            <p>No support tickets yet.</p>
            {!isSuperAdmin && (
              <button
                type="button"
                className="support-ticket-action-btn primary"
                onClick={() => onOpenSubmitPanel?.()}
              >
                Submit Support Ticket
              </button>
            )}
          </div>
        ) : (
          <div className="support-tickets-list">
            {tickets.map((ticket) => {
              const isExpanded = expandedId === ticket.id;
              const isOwner = Number(ticket.created_by) === Number(user?.id);
              const canVerifyClose = isOwner && ticket.status === 'Deployed';
              const displayStatus = statusDrafts[ticket.id] ?? ticket.status;
              const isPendingRejection =
                isSuperAdmin && displayStatus === 'Rejected' && ticket.status !== 'Rejected';

              return (
                <article key={ticket.id} className="support-ticket-card">
                  <div className="support-ticket-card-head">
                    <h3 className="support-ticket-card-title">#{ticket.id} — {ticket.title}</h3>
                    {isSuperAdmin ? (
                      <select
                        className="support-ticket-status-select"
                        value={displayStatus}
                        disabled={savingId === ticket.id}
                        aria-label={`Status for ticket ${ticket.id}`}
                        onChange={(event) => handleStatusSelectChange(ticket, event.target.value)}
                      >
                        {TICKET_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="support-ticket-status">{ticket.status}</span>
                    )}
                  </div>

                  {isPendingRejection && (
                    <div className="support-ticket-rejection-field">
                      <label htmlFor={`rejection-${ticket.id}`}>
                        <span>Rejection reason</span>
                        <textarea
                          id={`rejection-${ticket.id}`}
                          rows={3}
                          value={rejectionDrafts[ticket.id] || ''}
                          placeholder="Explain why this ticket is rejected (sent to the submitter)"
                          onChange={(event) =>
                            setRejectionDrafts((prev) => ({ ...prev, [ticket.id]: event.target.value }))
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="support-ticket-action-btn primary support-ticket-submit-rejection-btn"
                        disabled={savingId === ticket.id}
                        onClick={() => handleSubmitRejection(ticket)}
                      >
                        {savingId === ticket.id ? 'Submitting…' : 'Submit Rejection'}
                      </button>
                    </div>
                  )}

                  <div className="support-ticket-meta">
                    <span>Category: {ticket.category}</span>
                    {isSuperAdmin && <span>Role: {ticket.user_role}</span>}
                    <span>Created: {formatTicketDate(ticket.created_at)}</span>
                  </div>

                  <TicketEvidenceViewer
                    description={ticket.description}
                    evidenceUrl={ticket.evidence_url}
                    evidenceUrls={ticket.evidence_urls}
                  />

                  {isExpanded && (
                    <>
                      <p className="support-ticket-description">{ticket.description}</p>

                      {ticket.rejection_reason && (
                        <p className="support-ticket-description">
                          <strong>Rejection reason:</strong> {ticket.rejection_reason}
                        </p>
                      )}

                      {canVerifyClose && (
                        <div className="support-ticket-actions">
                          <button
                            type="button"
                            className="support-ticket-action-btn primary"
                            onClick={() => handleVerifyClose(ticket.id)}
                            disabled={closingId === ticket.id}
                          >
                            {closingId === ticket.id ? 'Closing…' : 'Verify & Close'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <div className="support-ticket-actions">
                    <button
                      type="button"
                      className="support-ticket-action-btn secondary"
                      onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                    >
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        show={Boolean(closeConfirm)}
        title="Close Ticket"
        message={
          closeConfirmTicket
            ? `Are you sure you want to close ticket #${closeConfirmTicket.id} — ${closeConfirmTicket.title}?`
            : CLOSE_CONFIRM_MESSAGE
        }
        warning="Closing will permanently delete this ticket record and all uploaded images. This cannot be undone."
        confirmLabel="Close Ticket"
        busyLabel="Closing..."
        cancelLabel="Cancel"
        isDeleting={isCloseBusy}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelCloseConfirm}
      />
    </div>
  );
};

export default SupportTicketsScreen;
